/**
 * ConfigContext — provides organisation terminology & branding config
 * to the entire app. Labels like "Camper", "Achievement Credit",
 * "Kerchief", etc. are all read from here so different camps can
 * customise them without code changes.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { configApi, type OrgConfig } from "../api/config";
import { useAuth } from "../hooks/useAuth";

// ─── Default fallback (matches Camp Highlands seed) ────────────────────────
const DEFAULTS: OrgConfig = {
  "org.name": "Camp Highlands",
  "org.shortName": "CH",
  "terms.participant": "Camper",
  "terms.participants": "Campers",
  "terms.group": "Cabin",
  "terms.groups": "Cabins",
  "terms.grouping": "Division",
  "terms.groupings": "Divisions",
  "terms.staff": "Counselor",
  "terms.staffPlural": "Counselors",
  "terms.activity": "Activity",
  "terms.activities": "Activities",
  "terms.credit": "Achievement Credit",
  "terms.credits": "Achievement Credits",
  "terms.creditShort": "AC",
  "terms.completion": "AC",
  "terms.completions": "ACs",
  "terms.honor": "Honor",
  "terms.honor.plural": "Honors",
  "terms.level": "Level",
  "terms.levels": "Levels",
  "terms.graduation": "Graduating",
  "terms.assessment": "Assessment",
  "terms.assessments": "Assessments",
  "terms.assessmentTemplate": "Assessment Template",
  "terms.kerchief": "Kerchief",
  "terms.kerchiefs": "Kerchiefs",
  "terms.progressReport": "Progress Report",
  "terms.kerchiefReport": "Kerchief Report",
};

interface ConfigContextValue {
  config: OrgConfig;
  /** Convenience getter — returns config[key] or the fallback default */
  t: (key: keyof typeof DEFAULTS | string, fallback?: string) => string;
  loading: boolean;
  refresh: () => Promise<void>;
}

export const ConfigContext = createContext<ConfigContextValue>({
  config: DEFAULTS,
  t: (key, fallback) => fallback ?? DEFAULTS[key] ?? key,
  loading: false,
  refresh: () => Promise.resolve(),
});

export function ConfigProvider({ children }: { children: React.ReactNode }) {
  const { organization } = useAuth();
  const orgSlug = organization?.slug;

  const [config, setConfig] = useState<OrgConfig>(DEFAULTS);
  const [loading, setLoading] = useState(false);

  const fetchConfig = useCallback((): Promise<void> => {
    if (!orgSlug) {
      setConfig(DEFAULTS);
      setLoading(false);
      return Promise.resolve();
    }
    setLoading(true);
    return configApi
      .get()
      .then((data) => setConfig({ ...DEFAULTS, ...data }))
      .catch(() => setConfig(DEFAULTS))
      .finally(() => setLoading(false));
  }, [orgSlug]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const t = useCallback(
    (key: string, fallback?: string): string =>
      config[key] ?? fallback ?? DEFAULTS[key] ?? key,
    [config]
  );

  return (
    <ConfigContext.Provider value={{ config, t, loading, refresh: fetchConfig }}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  return useContext(ConfigContext);
}
